// janus-engine/combat/start-roll.ts — R-117 slice L3b + R-128 (Janus lane N5b, 2026-10-01): the combat START decided in the browser.
//
// The port of what was left of ft_start_combat_and_roll_initiative() (was includes/functions/janus/encounter/start_encounter_combat.php)
// after slice L3a (start-plan.ts: the out-of-combat rest, the Orb Slots baseline / Ether pin, the opening charges). PHP's order, kept:
//   1. the island — the participants the PAGE resolved (roll-init/client-combat-membership.ts over the FT3D preview truth), stamped on
//      a working copy of the held encounter (combat id, active_combat_id), then L3a's rest + pre-trigger pools on it;
//   2. `triggers_after_combat_begins` on each member in roster order (actions/ability-use.ts resolveEngineFiredCasts, A3's export) — its
//      statuses land on the working copy BEFORE initiative, so a self status can move the roll (the reason PHP fired them first);
//   3. initiative — every living, public, uncontrolled PC rolls Initiative (resolveStandaloneSkillRoll) + a d1000 tiebreak, and its rest
//      is interrupted (roll_initiative_process_resting_status: Sleeping removed; Resting paused +1 h, or removed when permanent); the
//      creatures sort by CR DESC and the top one rolls for the side (+ a side d1000); higher first side; the alternating main order;
//   4. lairs by lair_dc DESC then name; the 20000 / 10000 / 5000 bands; each ranked member's start-of-turn RESET
//      (reset_encounter_creature_start_of_turn_variables: aps_total — lair 1, legendary base + PCs − 1, else max(3, stored) — and
//      aps = aps_total + Σ ap_change_at_start_of_turn, reaction 1); the opening + R-115 refill pools;
//   5. in_combat 1, round 1, the pointer on the top lair else main[0];
//   6. armed `combat_begins` contingencies of every resident (R-128): fizzle / top-up as ft_fire_contingency_triggers() judged it, then
//      the cast through resolveEngineFiredCasts (trigger mode, mana pre-paid, on its owner);
//   7. the FIRST combatant's start-of-turn block (resolveStartOfTurn, N1b's export): the reset with its no_actions / no_reactions
//      overrides and the START expiries. Its HOOKS — a recharge / decay / psi charge roll, regeneration (ability or status), the turn-begin
//      pass — are resolved for EVERY first combatant (End Turn's rule for every next actor) by the turn lane's start-mode ticks (turns/ticks.ts resolveStartTurnTicks, lane A8's export; wired by lane N5f, 2026-10-02 — R-130's first-turn
//      half) and ride the plan as `first.tick` (a verdict the commit saves after the START expiries, the turn commit's own order). What the
//      tick hands back, and the gates it does not serve, are frozen under `first.legacy` during preflight. R5a clears that list only on the
//      final plan and posts GM-private skip rows while preserving supported reset/expiry work. The commit receives no dispatch list.
// Every die goes through ONE RollSource. The plan probes max/min before real dice (owner ruling 56); an outcome only real dice reach
// keeps its existing refusal/skip handling. Pure: rows, readers and the engine functions in; the posted plan out.
import { isSelfStatusOrigin } from '../statuses/status-origin.js';
import { resolveEngineFiredCasts, resolveStandaloneSkillRoll, type AbilityUseReaders, type AbilityUseVerdict } from '../actions/ability-use.js';
import type { RollSource } from '../actions/roll-source.js';
import type { LogRow, StatusRow, StatusStackChange, CampaignClock } from '../ability/context.js';
import { resolveStartOfTurn, type EndTurnReaders, type StartOfTurnResult, type StartOfTurnOptions } from '../turns/end-turn.js';
import { resolveStartTurnTicks, type TurnTickInput, type TurnTickResult } from '../turns/ticks.js';
import { HandBack } from '../ability/context.js';
import { statusRows, statusItemId, statusPaused, type StatusRemoval } from '../statuses/expiry.js';
import { CONTINGENCY_ARMING_IDS } from '../sanguine/sanguine-gate.js';
import { contingencyParse, contingencyManaPlan } from '../sanguine/contingency-fire.js';
export { contingencyParse } from '../sanguine/contingency-fire.js';
import type { RefillOp } from '../charges/combat-refill.js';
import type { StartRestWrite } from './start-plan.js';
import type { ExitReaders } from './exit-plan.js';
import { skipUnported } from '../unported.js';
import { catalogSnapshot } from '../ability/frame-snapshot.js';
import { entryCharges } from '../actions/ability-preconditions.js';
import { ABILITY_CHARGE_BUCKETS, abilityEntryId } from '../ability/charges/charge-entry.js';

type Row = Record<string, unknown>;
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const scalar = (v: unknown): unknown => (Array.isArray(v) ? v[0] : v);
const upper = (v: unknown): string => String(scalar(v) ?? '').trim().toUpperCase();
const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
const phpEmpty = (v: unknown): boolean => { const s = scalar(v); return s === undefined || s === null || s === '' || s === 0 || s === '0' || s === false; };
const phpNumeric = (v: unknown): boolean => typeof v === 'number' ? Number.isFinite(v) : typeof v === 'string' && /^\s*[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?\s*$/.test(v);
/** PHP (int) of a stored scalar ('NA' → 0, '3abc' → 3). */
const phpInt = (v: unknown): number => {
  const s = scalar(v);
  if (typeof s === 'number') return Number.isFinite(s) ? Math.trunc(s) : 0;
  if (typeof s === 'boolean') return s ? 1 : 0;
  const m = /^\s*([+-]?\d+)/.exec(String(s ?? ''));
  return m ? parseInt(m[1]!, 10) : 0;
};
/** PHP 8 `<=>` for the two value kinds a CR / lair_dc can be: numeric strings / numbers compare numerically, anything else as strings. */
function phpCmp(a: unknown, b: unknown): number {
  const x = scalar(a) ?? 0, y = scalar(b) ?? 0;
  if (phpNumeric(x) && phpNumeric(y)) { const d = Number(x) - Number(y); return d < 0 ? -1 : d > 0 ? 1 : 0; }
  const sx = String(x), sy = String(y);
  return sx < sy ? -1 : sx > sy ? 1 : 0;
}
/** strcasecmp's sign (ASCII case folding). */
const strcasecmp = (a: string, b: string): number => { const x = a.toLowerCase(), y = b.toLowerCase(); return x < y ? -1 : x > y ? 1 : 0; };

export const INITIATIVE_SKILL_ID = '6E1A31A4-2D30-096B-BEA4-E2A1DC93A583';
export const RESTING_STATUS_ID = '3188FB3B-C860-43D4-9BA0-40364C460385';
export const SLEEPING_STATUS_ID = '095BC6BC-06F6-74E0-2B8A-FC1F32AF809B';
/** The universal Fortunate reaction (janus_trigger_inject_universal_fortunate — turns/ticks.ts's turn-begin pass reads its definition). */
export const FORTUNATE_ABILITY_ID = 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8';
/** The hooks the combat START's own first-turn block never ran (start_encounter_combat.php :374-426 had no lair / legendary branch, no
 *  turn-begin trigger and no timed-activity completion; the aps_total floor is applied here) — resolveStartOfTurn gates them for End Turn. */
const START_IGNORED_GATES = new Set(['aps-total-below-minimum', 'turn-begin-trigger-carrier']);
/** The first combatant's start-of-turn hooks the turn lane's start-mode ticks serve (end-turn.ts tickGates, minus the END-side dot / contest retry). */
const START_TICK_GATES = new Set(['ability-recharge-pending', 'ability-decay-pending', 'psi-regen-pending', 'regeneration-source', 'orb-channel-each-turn', 'status-loss-trigger-carrier']);
const LAIR_BAND = 20000, MAIN_BAND = 10000, SUMMON_BAND = 5000;
const MAX_APS = 127;

export interface StartParticipant { id: string; reason: string }
type Resolved = Extract<AbilityUseVerdict, { kind: 'resolved' }>;
/** The part of an engine verdict the combat commit saves (the channels below are the ONLY ones; anything else refuses pre-draw). */
export interface PostedVerdict {
  spawnRows?: import('../spawn/row-plan').SpawnRowPlan;
  statusTriggerListeners?: import('../ability/context.js').StatusTriggerListener[];
  rows: LogRow[];
  ec: Record<string, Record<string, unknown>>;
  statusesAdded: Array<StatusRow & { status_id: string }>;
  statusesRemoved: Array<{ status_item_id: string; target: string }>;
  statusStacks: StatusStackChange[];
  charges: Array<{ holder: string; item_id: string; before: number; charges: number }>;
  playerCharactersChanged: Array<{ item_id: string; skill_id: string; column: string; before: number; after: number }>;
}
export type ContingencyStep =
  | { kind: 'fire'; status_id: string; owner_id: string; spell_id: string; mana: { before: number; after: number } | null; verdict: PostedVerdict }
  | { kind: 'fizzle'; status_id: string; owner_id: string; mana: { before: number; after: number } | null; reason: string };
export interface ContingencyPlan { steps: ContingencyStep[]; skipped: Array<{ status_id: string; owner_id: string; reason: string }> }
/** Historical transport constant retained for exported API compatibility; raw storage has no step ceiling. */
export const CONTINGENCY_MAX_STEPS = 64;
/** The decisions the synthetic-dice passes made, FROZEN for the real pass (the A7 rule — Codex review A #1): a frame any synthetic pass handed
 *  back is not attempted on the real dice (no die is drawn for it), whatever those dice would have allowed. */
interface Frozen { beginsLegacy: Map<string, string>; contingencySkip: Map<string, string>; firstLegacy: Map<string, string> }
const noFrozen = (): Frozen => ({ beginsLegacy: new Map(), contingencySkip: new Map(), firstLegacy: new Map() });
// The supported reset/expiry projection survives a frozen unsupported hook. It is
// page-side state only; synthetic passes must retain the original gate reasons.
const SUPPORTED_FIRST = Symbol('combat-start-supported-first');
const MISSING_RESET_EFFECTS = Symbol('combat-start-missing-reset-effects');
type SupportedFirst = Pick<CombatStartPlan['first'], 'aps' | 'reaction' | 'removals'>;
type FirstWithSupported = CombatStartPlan['first'] & { [SUPPORTED_FIRST]?: SupportedFirst };

export interface RestingInterruption { target: string; remove: string[]; pause: { status_id: string; ends_before: string; ends: string; paused: string } | null }
export interface CombatStartPlan {
  combat_id: string;
  started_by: string | null;
  participants: StartParticipant[];
  /** Combat-begins verdicts. `legacy` is an internal preflight freeze list; the final plan empties it and emits GM-private skip verdicts. */
  begins: { verdicts: Array<{ actor_id: string; verdict: PostedVerdict }>; legacy: Array<{ actor_id: string; reason: string }> };
  initiative: {
    /** In roll order; a PC's resting interruption rides ITS roll (saved right after that roll's verdict — the planning order). */
    rolls: Array<{ actor_id: string; total: number; tie: number; side: 'player' | 'creature'; verdict: PostedVerdict; resting?: RestingInterruption }>;
    first_side: 'players' | 'creatures';
    lairs: string[]; main: string[]; summons: string[];
  };
  /** Every ranked member's start-of-turn reset + initiative — every value the save writes is posted (lane N5e, coordinator Codex round 4: the
   *  reset was literals in PHP's UPDATE). */
  members: Array<{ id: string; aps: number; aps_total: number; creature_initiative: number; weapon_attack: number; head_attack: number; reaction: 0 | 1; movement_pct: number }>;
  /** The pre-image the save CASes (lane N5e — coordinator Codex round 4 #2): every encounter_creatures column a posted verdict's `ec` writes,
   *  as this page read it BEFORE the plan (the begins triggers, the initiative rolls, the contingency fires) — a write that landed on that
   *  column between this plan and the save's row lock refuses the start (409) instead of being overwritten. */
  ec_before: Record<string, Record<string, unknown>>;
  /** Expiry log rows absent from the first tick; PHP only saves these browser-decided receipts. */
  first_removal_rows?: LogRow[];
  encounter: { in_combat: 1; current_round: 1; current_encounter_creature_id_turn: string };
  /** R-128: the armed contingencies, as ONE ordered list (the commit saves the steps in this order — each mana CAS reads the previous step's
   *  write; Codex review A #2) + the ones that stay armed. */
  contingencies: ContingencyPlan;
  first: { id: string; legacy: string[] | null; aps: number; aps_total: number; reaction: 0 | 1; weapon_attack: number; head_attack: number; movement_pct: number; removals: StatusRemoval[];
    /** Start-mode tick verdict, including GM-private skips. `legacy` is internal preflight state and null on every final plan. */
    tick: PostedVerdict | null };
}
/** The encounter_creatures columns a posted verdict may write, by kind (combat_truth_start.php FT_COMBAT_VERDICT_INT_EC / _DEC_EC / _TEXT_EC). */
const VERDICT_DEC_EC = new Set(['x_pos', 'y_pos', 'z_pos']);
const VERDICT_TEXT_EC = new Set(['preferred_speed_type', 'spell_list_prepared', 'orb_queue', 'team', 'controller_encounter_creature_id']);
/** One column of a row as the save compares it: an integer column as PHP's (int) (absent → 0), a coordinate as a number (absent → null), a text
 *  column as its stored text (absent → null; the orb queue as its JSON text — the save compares the decoded queue). */
function ecPreImage(row: Row | undefined, col: string): unknown {
  const ec = row ? ecOf(row) : {};
  if (VERDICT_DEC_EC.has(col)) {
    const v = scalar(asRow(row?.position)?.[col] ?? ec[col]);
    return v === undefined || v === null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v);
  }
  if (VERDICT_TEXT_EC.has(col)) {
    const v = ec[col];
    if (v === undefined || v === null || v === '') return null;
    if (col === 'orb_queue') return typeof v === 'string' ? v : JSON.stringify(v);
    return String(scalar(v) ?? '');
  }
  return phpInt(ec[col]);
}
export interface CombatStartInput {
  encounterId: string;
  combatId: string;
  participants: StartParticipant[];
  startedBy: string | null;
  /** L3a: the out-of-combat rest and the pools reset BEFORE the begins triggers (start-plan.ts). */
  rest: StartRestWrite[];
  pre: RefillOp[];
  /** L3a + R-115: the opening charges then the per-combat refill (last write per pool wins) — before the first combatant's block. */
  charges: RefillOp[];
  rollPrivate: number;
}
/** The page's row mutators (statuses / charges), injected so the module stays pure. */
export interface CombatStartAppliers {
  upsertStatus: (row: Row, entry: Row) => void;
  removeStatus: (row: Row, statusId: string) => boolean;
  setCharges: (row: Row, itemId: string, current: number) => void;
}
export interface CombatStartReaders {
  /** The ability engine's readers (A3) — definition lookups, layer readers, the campaign clock — over whatever members map it is handed. */
  ability: AbilityUseReaders;
  /** End Turn's readers (N1b) over the given working copy (the start-of-turn resolver reads the roster through them). */
  turn: (encounter: Row) => EndTurnReaders;
  exit: ExitReaders;
  /** The campaign clock now (combat freezes it) and its calendar arithmetic. */
  clockNow: () => CampaignClock | null;
  addSeconds: (clock: CampaignClock, seconds: number) => CampaignClock;
  uuid: () => string;
  /** The page's bounded fetch policy for optional hooks. Required Initiative data never uses this allowance. */
  canFetchOptionalDefinition?: (id: string) => boolean;
  /** Fresh raw charge SELECTs. A held empty map proves no saved pool; a missing holder proves nothing. */
  chargeRows?: ReadonlyMap<string, Readonly<Record<string, unknown>>>;
}
export interface CombatStartEngine {
  engineFired: typeof resolveEngineFiredCasts;
  skillRoll: typeof resolveStandaloneSkillRoll;
  startOfTurn: (row: Row, read: EndTurnReaders, opts?: StartOfTurnOptions) => StartOfTurnResult;
  /** The turn lane's start-mode ticks (R-130 first-turn half). Optional for the unit tests' engines; absent = resolveStartTurnTicks. */
  startTicks?: typeof resolveStartTurnTicks;
}
export const COMBAT_START_ENGINE: CombatStartEngine = { engineFired: resolveEngineFiredCasts, skillRoll: resolveStandaloneSkillRoll, startOfTurn: resolveStartOfTurn, startTicks: resolveStartTurnTicks };
export type CombatStartOutcome =
  | { kind: 'plan'; plan: CombatStartPlan; warnings: string[] }
  /** Definitions the page must hold first (fetch, then plan again) — decided on the synthetic dice, before any real one. */
  | { kind: 'missing'; ids: string[] }
  | { kind: 'refuse'; reason: string; message: string }
  /** OWNER RULING 2026-10-02 (lane A11): a synthetic pass met an optional trigger (a reaction) of a player's creature in a combat-begins frame that
   *  nobody has answered — the page asks its controller (≤ 5 s, websocket/trigger-consent.ts) and plans again with the answer fixed. */
  | { kind: 'consent' };

const ecOf = (row: Row): Row => asRow(row.encounter_creatures) ?? {};
const idOf = (row: Row): string => upper(ecOf(row).encounter_creature_id);
const itemType = (row: Row): string => String(scalar(ecOf(row).item_type) ?? scalar(asRow(row.items)?.item_type) ?? '').toLowerCase();
function rosterOf(encounter: Row): Row[] {
  return (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as unknown[] : []).map(asRow).filter((r): r is Row => !!r && !!idOf(r));
}
const bucketOf = (encounter: Row): Row => (encounter.encounters ??= {}) as Row;
const nameOf = (row: Row): string => String(scalar(ecOf(row).encounter_creature_name) ?? scalar(asRow(row.items)?.item_name) ?? '');
const living = (row: Row): boolean => phpEmpty(ecOf(row).encounter_creature_dead) && phpEmpty(ecOf(row).encounter_creature_deleted);

class PlanRefusal extends Error { constructor(public reason: string, public userMessage: string) { super(reason); } }
class PlanMissing extends Error { constructor(public ids: string[]) { super('missing'); } }
class PlanConsent extends Error { constructor() { super('consent'); } }

/** Unsupported channels are frozen before real dice and converted to GM skips in the final start plan. */
function unsavable(v: Resolved, encounterId: string): string | null {
  if (v.inventory.length) return 'inventory';
  if (v.deathTail.length) return 'death-tail';
  if (v.concentrationBreaks.length) return 'concentration-break';
  if (v.counterspell) return 'counterspell';
  if (v.turnTransition || v.turnDeathNone) return 'turn-transition';
  if ((v as unknown as { forcedMovements?: unknown[] }).forcedMovements?.length) return 'forced-movement';
  if (v.rows.some(r => (r as LogRow).random_table != null || ['random_table', 'random_loot_table', 'random_encounter_table', 'random_name_table'].includes(r.roll_type_1))) return 'random-table-row';
  // Creature-bound aura anchors ride status writes; encounter anchors/rows exceed this member-only start frame.
  const enc = upper(encounterId);
  if (v.statusesAdded.some(s => Number(s.status_aoe_source ?? 0) !== 0 && (upper(s.target) === enc || !UUID_RE.test(upper(s.status_aoe_type))))) return 'zone-anchor';
  if (v.rows.some(r => upper((r as unknown as Row).roll_to) === enc || upper((r as unknown as Row).roll_by) === enc)) return 'encounter-row';
  return null;
}
/** R-130 first-turn half (lane N5f, 2026-10-02) — the first combatant's start-of-turn hooks through the turn lane's start-mode ticks
 *  (turns/ticks.ts resolveStartTurnTicks: the recharge / decay / psi charge rolls, regeneration, the turn-begin pass — its own pre-draw dry runs)
 *  on a copy of the working encounter whose first row already holds the first block's reset (so the verdict carries only what the hooks
 *  changed). The tick's own `start_turn` marker row is dropped: the commit writes the block's start_turn row itself (the turn commit's rule).
 *  An unsupported channel returns a reason, frozen before real dice and turned into a skip only in the final plan. */
function firstTurnTicks(work: Row, firstRow: Row, aps: number, reaction: 0 | 1, removals: StatusRemoval[], read: CombatStartReaders, roll: RollSource,
  engine: CombatStartEngine, encounterId: string, real = false, preflight?: TurnTickInput['preflight']): PostedVerdict | string {
  // Synthetic ticks include the adapter's channel checks before real dice: Initiative may pick
  // a first combatant the earlier passes did not inspect (A7). The three resolutions differ only in their dice, so they share ONE tick dry
  // phase (turns/ticks.ts `preflight` — fix-bug 2026-10-13: the start re-ran the identical dry phase per outcome).
  if (real) {
    const shared: NonNullable<TurnTickInput['preflight']> = {};
    for (const pattern of START_DRY_PATTERNS) {
      const dry = firstTurnTicks(work, firstRow, aps, reaction, removals, read, syntheticDice(pattern), engine, encounterId, false, shared);
      if (typeof dry === 'string') return dry;
    }
    preflight = shared;
  }
  const tickWork = catalogSnapshot(work)();
  const roster = rosterOf(tickWork);
  const first = roster.find(r => idOf(r) === idOf(firstRow));
  if (!first) return 'turn-tick:member-not-held';
  Object.assign((first.encounter_creatures ??= {}) as Row, { aps, reaction, weapon_attack: 0, head_attack: 0, movement_pct: 0 });
  delete (first.encounter_creatures as Row).aps_used;
  let t: TurnTickResult;
  try {
    t = (engine.startTicks ?? resolveStartTurnTicks)({ encounter: tickWork, roster, nextId: idOf(first), nextReaction: reaction,
      startRemoved: new Set(removals.map(r => upper(r.status_id))), startExpiries: removals, entries: read.turn(tickWork).abilityEntries(first), read: { ability: read.ability, roll },
      ...(preflight ? { preflight } : {}) });
  } catch (error) {
    if (error instanceof HandBack) return error.reason;
    return 'turn-tick:' + (error instanceof Error && /^refusal:/.test(error.message) ? 'refused' : 'exception');
  }
  // The commit's supported channels; anything else becomes a frozen skip reason.
  if (t.statusesChanged.length) return 'turn-tick:stack-change';
  const enc = upper(encounterId);
  const rows = t.rows.filter(r => r.roll_type_1 !== 'start_turn' && r.roll_type_1 !== 'end_turn');
  if (rows.some(r => r.random_table != null || ['random_table', 'random_loot_table', 'random_encounter_table', 'random_name_table'].includes(r.roll_type_1))) return 'turn-tick:random-table-row';
  if (rows.some(r => upper(r.roll_to) === enc || upper(r.roll_by) === enc)) return 'turn-tick:encounter-row';
  if (t.statusesAdded.some(s => Number(s.status_aoe_source ?? 0) !== 0)) return 'turn-tick:zone-anchor';
  const charges: PostedVerdict['charges'] = [];
  for (const c of t.charges) {
    const before = (c as { before?: unknown }).before;
    if (typeof before !== 'number' || !Number.isInteger(before) || before < 0 || !Number.isInteger(c.charges) || c.charges < 0) return 'turn-tick:charge-before-not-held';
    charges.push({ holder: upper(c.holder), item_id: upper(c.item_id), before, charges: c.charges });
  }
  const verdict: PostedVerdict = {
    ...(t.spawnRows ? { spawnRows: t.spawnRows } : {}),
    rows, ec: t.ec,
    statusesAdded: t.statusesAdded.map(s => ({ ...s, status_id: /^[0-9A-F-]{36}$/i.test(String((s as unknown as Row).status_id ?? '')) ? upper((s as unknown as Row).status_id) : read.uuid() })),
    statusesRemoved: t.statusesRemoved, statusStacks: [], charges, playerCharactersChanged: [],
    ...(t.statusTriggerListeners?.length ? { statusTriggerListeners: t.statusTriggerListeners } : {}),
  };
  // OWNER RULING 2026-10-03 (lane A11 — ticks.ts deferStartModeFortunate): the first combatant's deferred Fortunate offer rides the verdict as a
  // NON-enumerable page-side field (never serialized into the start commit); the start page offers it once the start is saved
  if (t.fortunateOffers?.length) Object.defineProperty(verdict, 'fortunateOffers', { value: [...t.fortunateOffers], enumerable: false });
  return verdict;
}

/** A `preflight:…:<id>` hand-back naming a definition the page does not hold. */
const missingFrom = (reason: string): string | null => /(?:not-held|missing)[^0-9A-F]*([0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12})/i.exec(reason)?.[1]?.toUpperCase() ?? null;

/** The verdict as posted (status ids minted here, so a later step of the SAME plan can name a status an earlier one added). */
function post(v: Resolved, uuid: () => string): PostedVerdict {
  return {
    ...(v.spawnRows ? { spawnRows: v.spawnRows } : {}),
    rows: v.rows, ec: v.ec,
    statusesAdded: v.statusesAdded.map(s => ({ ...s, status_id: /^[0-9A-F-]{36}$/i.test(String((s as unknown as Row).status_id ?? '')) ? upper((s as unknown as Row).status_id) : uuid() })),
    statusesRemoved: v.statusesRemoved, statusStacks: v.statusStacks ?? [], charges: v.charges, playerCharactersChanged: v.playerCharactersChanged ?? [],
    ...(v.statusTriggerListeners?.length ? { statusTriggerListeners: v.statusTriggerListeners } : {}),
  };
}
const emptyVerdict = (p: PostedVerdict): boolean => !p.rows.length && !Object.keys(p.ec).length && !p.statusesAdded.length && !p.statusesRemoved.length
  && !p.statusStacks.length && !p.charges.length && !p.playerCharactersChanged.length;
const skippedVerdict = (actor: string, phase: string, reasons: readonly string[]): PostedVerdict => ({
  rows: reasons.map(reason => ({ ...skipUnported('combat-start:' + phase + ':' + reason, { actorId: actor }), roll_by: actor, roll_to: actor })),
  ec: {}, statusesAdded: [], statusesRemoved: [], statusStacks: [], charges: [], playerCharactersChanged: [],
});

/** A posted verdict applied to the working copy, so the next step of the plan reads what this one wrote. */
function applyVerdict(work: Row, p: PostedVerdict, ap: CombatStartAppliers): void {
  const rows = new Map(rosterOf(work).map(r => [idOf(r), r]));
  for (const [id, changes] of Object.entries(p.ec)) { const r = rows.get(upper(id)); if (r) Object.assign((r.encounter_creatures ??= {}) as Row, changes); }
  for (const s of p.statusesAdded) {
    const ends = String(s.ends ?? '0-0-0-0-0-0').split('-').map(n => Number(n) || 0);
    const entry: Row = { ...s, status_target_id: upper(s.target), status_caster_id: upper(s.caster) };
    ['year', 'month', 'day', 'hour', 'minute', 'second'].forEach((u, i) => { entry[`status_ends_campaign_${u}`] = ends[i] ?? 0; });
    delete entry.target; delete entry.caster; delete entry.ends;
    for (const holder of new Set([upper(s.target), upper(s.caster)])) { const r = rows.get(holder); if (r) ap.upsertStatus(r, entry); }
  }
  for (const s of p.statusesRemoved) {
    const r = rows.get(upper(s.target)); if (!r) continue;
    for (const st of statusRows(r, 'target')) if (statusItemId(st) === upper(s.status_item_id)) ap.removeStatus(r, upper(st.status_id));
  }
  for (const s of p.statusStacks) { const r = rows.get(upper(s.target)); if (!r) continue; for (const st of statusRows(r, 'target')) if (upper(st.status_id) === upper(s.status_id)) st.status_stack = s.status_stack; }
  for (const c of p.charges) { const r = rows.get(upper(c.holder)); if (r) ap.setCharges(r, upper(c.item_id), c.charges); }
  for (const c of p.playerCharactersChanged) { const r = rows.get(upper(c.item_id)); const pc = r && asRow(r.player_characters); if (pc) pc[c.column] = c.after; }
}

/** ft_orb_fire_ap_bonus(): +1 AP per channeled fire orb in the queue. */
function orbFireBonus(row: Row): number {
  let raw: unknown = ecOf(row).orb_queue;
  if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { raw = []; } }
  return Array.isArray(raw) ? raw.filter(o => String(typeof o === 'string' ? o : asRow(o)?.slug ?? '').toLowerCase() === 'fire').length : 0;
}

/** reset_encounter_creature_start_of_turn_variables() for ONE member (reset_encounter_creature_start_of_turn_variables.php): aps_total — a lair 1,
 *  a legendary creature its template `aps` (min 3) + (living public uncontrolled PCs − 1), anything else max(3, stored) — and aps = aps_total +
 *  Σ ap_change_at_start_of_turn (statuses + always-on passives, a combat-begins carrier passive skipped — Hot Start's live status owns it) + a fire
 *  orb's +1, clamped 0..127. Shared by the START (every ranked member) and the mid-combat JOIN (R-131 — every joiner, janus-engine/combat/
 *  join-plan.ts). A definition the start-of-turn resolver could not read is added to `missing` (the caller fetches it and plans again). */
export function memberStartOfTurnReset(row: Row, work: Row, read: Pick<CombatStartReaders, 'turn' | 'exit'>, engine: Pick<CombatStartEngine, 'startOfTurn'>,
  missing: Set<string>): { aps: number; aps_total: number } {
  const pcCount = (): number => rosterOf(work).filter(r => itemType(r) === 'player_character' && phpEmpty(ecOf(r).encounter_creature_dead)
    && phpEmpty(ecOf(r).encounter_creature_private) && phpEmpty(ecOf(r).controller_encounter_creature_id)).length;
  const type = itemType(row);
  let total: number;
  if (type === 'lair') total = 1;
  else if (type === 'creature' && !phpEmpty(read.exit.value(row, 'legendary_creature'))) total = Math.max(3, phpInt(read.exit.value(row, 'aps') ?? 3)) + Math.max(0, pcCount() - 1);
  else { const stored = ecOf(row).aps_total; total = Math.max(3, stored === undefined || stored === null ? 3 : phpInt(stored)); }
  const sot = engine.startOfTurn(row, read.turn(work), { clockAdvanceSeconds: 0 });
  for (const g of sot.gates) if (g.startsWith('definition-missing:')) missing.add(upper(g.slice('definition-missing:'.length)));
  // ft_passive_ap_change_at_start_of_turn_total() skips a passive that is itself a combat-begins carrier (its live status owns the bonus).
  const delta = sot.ap_change_sources.filter(s => !(s.kind === 'passive' && !phpEmpty(read.exit.value(read.exit.definition(s.id), 'triggers_after_combat_begins'))))
    .reduce((sum, s) => sum + s.delta, 0) + orbFireBonus(row);
  return { aps: Math.max(0, Math.min(MAX_APS, total + delta)), aps_total: total };
}

export function planCombatStart(input: CombatStartInput, encounter: Row, read: CombatStartReaders, ap: CombatStartAppliers, roll: RollSource,
  engine: CombatStartEngine = COMBAT_START_ENGINE): CombatStartOutcome {
  const passes = combatStartPasses(input, encounter, read, ap, roll, engine);
  for (;;) { const step = passes.next(); if (step.done) return step.value; }
}

/** The page's driver: the SAME passes in the same order, with the event loop let in between them. A start is three whole-plan passes (two
 *  synthetic, then the real dice); run as one task, a twelve-combatant start held the FT3D tab's main thread for tens of seconds (fix-bug
 *  2026-10-13). The passes read ONE snapshot of the encounter; the readers, though, read live page state, so `unchanged()` (the caller's
 *  watch) is asked after every pause and null comes back when anything moved — always BEFORE the real pass, which then runs without a pause,
 *  so no real die is ever drawn for a plan that is thrown away. The trigger-consent answers only change inside the page's awaited ask round
 *  (websocket/trigger-consent.ts), never in a pause. */
export async function planCombatStartYielding(input: CombatStartInput, encounter: Row, read: CombatStartReaders, ap: CombatStartAppliers,
  roll: RollSource, engine: CombatStartEngine = COMBAT_START_ENGINE, unchanged: (beforeReal: boolean) => boolean = () => true,
  pause: () => Promise<void> = yieldToPage): Promise<CombatStartOutcome | null> {
  // No up-front copy of the encounter (initfreeze STEP G, 2026-10-15: a 20 MB owner encounter's JSON copy cost 1.3 s of one task). The ONE
  // snapshot the passes read is serialized when the first pass starts — synchronously, before the first pause (combatStartPasses →
  // catalogSnapshot); what it keeps by reference is only the catalogs, which the engine never writes (the FREEZE golden), and a page change to
  // them during a pause is a moved input that the caller's watch reports before the real pass.
  const passes = combatStartPasses(input, encounter, read, ap, roll, engine);
  for (;;) {
    const step = passes.next();
    if (step.done) return step.value;
    await pause();
    if (!unchanged(step.value)) return null; // step.value: the next pass is the real one
  }
}

/** A macrotask turn (input, paint and socket frames run), where the host has a timer; a host without one (a test VM) just continues. */
const yieldToPage = (): Promise<void> => new Promise<void>(resolve => {
  const timer = (globalThis as { setTimeout?: (fn: () => void, ms: number) => unknown }).setTimeout;
  if (typeof timer === 'function') timer(resolve, 0); else resolve();
});

function* combatStartPasses(input: CombatStartInput, encounter: Row, read: CombatStartReaders, ap: CombatStartAppliers, roll: RollSource,
  engine: CombatStartEngine): Generator<boolean, CombatStartOutcome, void> {
  // Probe the whole plan on max/min before real dice (owner ruling 56); freeze the unsupported effects those outcomes reach.
  const frozen = noFrozen();
  const fresh = catalogSnapshot(encounter);
  for (const [index, pattern] of START_DRY_PATTERNS.entries()) {
    const dry = runPlan(input, fresh(), read, ap, syntheticDice(pattern), engine, frozen);
    if (dry.kind !== 'plan') return dry;
    for (const l of dry.plan.begins.legacy) frozen.beginsLegacy.set(l.actor_id, l.reason);
    for (const k of dry.plan.contingencies.skipped) frozen.contingencySkip.set(k.status_id, k.reason);
    // a first-turn block a synthetic pass handed back (its ticks) is not attempted on the real dice for that actor (the A7 rule)
    if (dry.plan.first.legacy && !frozen.firstLegacy.has(dry.plan.first.id)) frozen.firstLegacy.set(dry.plan.first.id, dry.plan.first.legacy.join(','));
    yield index === START_DRY_PATTERNS.length - 1; // true: the real pass is next
  }
  // OWNER RULING 2026-10-02 (lane A11): the synthetic passes asked every reaction they met (a 'consent' outcome → the page asked → planned again);
  // on the REAL dice the answers are final — a reaction only the real dice reach declines unasked (silence = decline), never a re-roll
  if (read.ability.triggerConsent) read.ability.triggerConsent.final = true;
  const real = runPlan(input, fresh(), read, ap, roll, engine, frozen, true);
  // a definition only the REAL dice reached (an outcome-dependent one): refused, never re-planned on fresh dice (Codex review A #1)
  if (real.kind === 'missing') return { kind: 'refuse', reason: 'definitions-outcome-dependent', message: 'Part of this fight\'s opening needs data the page has not loaded — try again in a moment.' };
  if (real.kind !== 'plan') return real;
  // R5a: transform only the final real plan. Clearing the synthetic legacy lists
  // earlier would erase the freeze and allow an unsupported hook to draw again.
  for (const unsupported of real.plan.begins.legacy) real.plan.begins.verdicts.push({
    actor_id: unsupported.actor_id, verdict: skippedVerdict(unsupported.actor_id, 'begins', [unsupported.reason]),
  });
  real.plan.begins.legacy = [];
  const missingResets = (real.plan as CombatStartPlan & { [MISSING_RESET_EFFECTS]?: Map<string, string[]> })[MISSING_RESET_EFFECTS];
  for (const [actor, reasons] of missingResets ?? []) if (actor !== real.plan.first.id) real.plan.begins.verdicts.push({
    actor_id: actor, verdict: skippedVerdict(actor, 'reset', reasons),
  });
  const first = real.plan.first as FirstWithSupported;
  if (first.legacy?.length) {
    const reasons = first.legacy;
    Object.assign(first, first[SUPPORTED_FIRST] ?? {}, { legacy: null, tick: skippedVerdict(first.id, 'first-turn', reasons) });
  }
  const expiryReceipts = [...(first.tick?.rows ?? [])];
  real.plan.first_removal_rows = first.removals.flatMap(removal => {
    const index = expiryReceipts.findIndex(row => row.roll_type_1 === removal.log_key && upper(row.roll_item_id) === removal.item_id
      && upper(row.roll_by) === removal.log_by && row.roll_result === 0 && !row.roll_to);
    if (index >= 0) { expiryReceipts.splice(index, 1); return []; }
    return [{ roll_type_1: removal.log_key, roll_type_2: null, roll_item_id: removal.item_id, roll_by: removal.log_by, roll_to: null, roll_result: 0 }];
  });
  return real;
}
/** Owner ruling 56 (2026-10-15): start plans and first-turn tick preflight use max/min; the traced corpus freezes the same entries.
 *  Exit contingencies retain all eight patterns, matching the cast and turn lanes, until separately traced. */
const START_DRY_PATTERNS = ['max', 'min'] as const;
const EXIT_DRY_PATTERNS = ['max', 'min', 'alt1-hi', 'alt1-lo', 'alt2-hi', 'alt2-lo', 'alt3-hi', 'alt3-lo'] as const;
function syntheticDice(pattern: (typeof START_DRY_PATTERNS | typeof EXIT_DRY_PATTERNS)[number]): RollSource {
  let n = 0;
  const high = (): boolean => {
    if (pattern === 'max' || pattern === 'min') return pattern === 'max';
    const period = Number(pattern[3]);
    const firstRun = Math.floor(n / period) % 2 === 0;
    return pattern.endsWith('-hi') ? firstRun : !firstRun;
  };
  return { d: die => { const face = high() ? die : 1; n += 1; return face; }, fraction: () => { const v = high() ? 0.999999 : 0; n += 1; return v; } };
}
const UUID_RE = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

function runPlan(input: CombatStartInput, work: Row, read: CombatStartReaders, ap: CombatStartAppliers, roll: RollSource, engine: CombatStartEngine,
  frozen: Frozen, real = false): CombatStartOutcome {
  try { return { kind: 'plan', ...plan(input, work, read, ap, roll, engine, frozen, real) }; }
  catch (error) {
    if (error instanceof PlanMissing) return { kind: 'missing', ids: error.ids };
    if (error instanceof PlanConsent) return { kind: 'consent' };
    if (error instanceof PlanRefusal) return { kind: 'refuse', reason: error.reason, message: error.userMessage };
    throw error;
  }
}

function plan(input: CombatStartInput, work: Row, read: CombatStartReaders, ap: CombatStartAppliers, roll: RollSource, engine: CombatStartEngine,
  frozen: Frozen, real: boolean): { plan: CombatStartPlan; warnings: string[] } {
  const warnings: string[] = [];
  const combatId = upper(input.combatId);
  const participantIds = new Set(input.participants.map(p => upper(p.id)));
  const missing = new Set<string>();
  // The rows as the page read them before any step of the plan touched the working copy — the pre-image `ec_before` is cut from.
  // A DEEP copy (Codex N5e TS #1): a column holding an array (the orb queue) must not alias a value a later step mutates in place.
  const preImage = new Map(rosterOf(work).map(r => [idOf(r), clone({ encounter_creatures: ecOf(r), position: asRow(r.position) ?? undefined }) as Row]));
  // ---- 1. membership on the working copy + L3a's rest and pre-trigger pools ----
  bucketOf(work).active_combat_id = combatId;
  if ('active_combat_id' in work) work.active_combat_id = combatId;
  for (const row of rosterOf(work)) { const ec = (row.encounter_creatures ??= {}) as Row; if (participantIds.has(idOf(row))) ec.combat_id = combatId; else delete ec.combat_id; }
  const byId = (): Map<string, Row> => new Map(rosterOf(work).map(r => [idOf(r), r]));
  const isMember = (row: Row): boolean => upper(ecOf(row).combat_id) === combatId;
  for (const w of input.rest) { const r = byId().get(upper(w.id)); if (!r || !isMember(r)) continue; const ec = r.encounter_creatures as Row; if (w.aps !== undefined) { ec.aps = w.aps; delete ec.aps_used; } if (w.reaction !== undefined) ec.reaction = w.reaction; }
  for (const op of input.pre) { const r = byId().get(upper(op.holder)); if (r && isMember(r)) ap.setCharges(r, upper(op.item_id), op.to); }
  // Bare carrier refs use fresh saved charges, then the cast lane's default ONLY when
  // a successful SELECT proves no saved row. Existing counts (including zero) stay held.
  if (read.chargeRows?.size) for (const row of rosterOf(work)) {
    if (!isMember(row)) continue;
    for (const bucket of ABILITY_CHARGE_BUCKETS) for (const raw of read.ability.entries(row, bucket)) {
      const id = abilityEntryId(raw);
      const definition = read.ability.definition(id);
      if (!definition || !read.ability.has(definition, 'triggers_after_combat_begins')) continue;
      const pool = entryCharges(asRow(raw) ?? { item_id: id }, id, { value: read.ability.value, resolveItem: id => read.ability.definition(upper(id)) });
      if (!pool || pool.currentHeld) continue;
      const saved = read.chargeRows?.get(idOf(row));
      if (!saved) continue;
      if (Object.prototype.hasOwnProperty.call(saved, id)) { ap.setCharges(row, id, phpInt(saved[id])); continue; }
      if (phpNumeric(pool.current)) ap.setCharges(row, id, phpInt(pool.current));
    }
  }
  const members = (): Map<string, Row> => byId();
  const resolved = (v: AbilityUseVerdict, what: string): Resolved | string => {
    if (v.kind === 'refuse') return `${what}:refused:${v.message}`;
    if (v.kind !== 'resolved') {
      const need = missingFrom(v.reason);
      if (need && !real && read.canFetchOptionalDefinition?.(need) !== false) throw new PlanMissing([need]);
      return v.reason;
    }
    const bad = unsavable(v, input.encounterId);
    return bad ? `${what}:unsavable:${bad}` : v;
  };

  // ---- 2. triggers_after_combat_begins, member by member in roster order (janus_trigger per combatant) ----
  const begins: CombatStartPlan['begins'] = { verdicts: [], legacy: [] };
  for (const row of rosterOf(work)) {
    if (!isMember(row)) continue;
    const id = idOf(row);
    if (frozen.beginsLegacy.has(id)) { begins.legacy.push({ actor_id: id, reason: frozen.beginsLegacy.get(id)! }); continue; }
    const v = resolved(engine.engineFired({ encounterId: input.encounterId, rollPrivate: input.rollPrivate, afterCombatBegins: [id] }, work, members(), read.ability, roll), 'begins');
    // OWNER RULING 2026-10-02 (lane A11): a reaction of a player's creature in this frame needs its controller's answer — asked before any real
    // die (a synthetic pass); on the REAL dice the reader is final (planCombatStart), so an unforeseen one declines inside the frame
    if (v === 'preflight:trigger-consent' && !real) throw new PlanConsent();
    if (typeof v === 'string') {
      // Freeze unsupported effects on synthetic passes; only the final plan converts them to skips.
      begins.legacy.push({ actor_id: id, reason: v }); continue;
    }
    const p = post(v, read.uuid);
    if (emptyVerdict(p)) continue;
    applyVerdict(work, p, ap);
    begins.verdicts.push({ actor_id: id, verdict: p });
  }
  if (missing.size) throw new PlanMissing([...missing]);

  // ---- 3. initiative (gather_and_roll_main_participants → the player loop → the creature side) ----
  const players: Row[] = [], creatures: Array<{ id: string; cr: unknown }> = [], lairs: Row[] = [];
  for (const row of rosterOf(work)) {
    if (!isMember(row) || !living(row)) continue;
    const lair = itemType(row) === 'lair';
    if ((!phpEmpty(ecOf(row).encounter_creature_private) && !lair) || !phpEmpty(ecOf(row).controller_encounter_creature_id)) continue;
    if (lair) lairs.push(row);
    else if (itemType(row) === 'player_character') players.push(row);
    else creatures.push({ id: idOf(row), cr: scalar(read.exit.value(row, 'cr')) ?? 0 });
  }
  const rolls: CombatStartPlan['initiative']['rolls'] = [];
  const rollFor = (id: string, side: 'player' | 'creature'): { total: number; tie: number } => {
    const out = engine.skillRoll({ encounterId: input.encounterId, creatureId: id, skillIds: [INITIATIVE_SKILL_ID], rollItemId: INITIATIVE_SKILL_ID, rollPrivate: input.rollPrivate },
      work, members(), read.ability, roll);
    if (out.kind !== 'resolved') {
      const need = missingFrom(out.reason);
      if (need && out.reason.startsWith('preflight:')) throw new PlanMissing([need]);
      throw new PlanRefusal('initiative-handback:' + out.reason, `Initiative for ${nameOf(byId().get(id) ?? {}) || 'a combatant'} cannot be rolled in the browser yet (${out.reason}).`);
    }
    const bad = unsavable(out, input.encounterId);
    if (bad) throw new PlanRefusal('initiative-unsavable:' + bad, `Initiative for ${nameOf(byId().get(id) ?? {}) || 'a combatant'} cannot be saved by the combat lane (${bad}).`);
    const p = post(out, read.uuid);
    applyVerdict(work, p, ap);
    const tie = roll.d(1000); // rand(1, 1000)
    rolls.push({ actor_id: id, total: out.total, tie, side, verdict: p });
    return { total: out.total, tie };
  };
  const playerInit: Array<{ id: string; init: number; tie: number }> = [];
  for (const row of players) {
    const id = idOf(row);
    const r = rollFor(id, 'player');
    playerInit.push({ id, init: r.total, tie: r.tie });
    const rest = restingInterruption(byId().get(id) ?? row, read);
    if (rest) {
      rolls[rolls.length - 1]!.resting = rest;
      const live = byId().get(id);
      if (live) {
        for (const sid of rest.remove) ap.removeStatus(live, sid);
        if (rest.pause) for (const st of statusRows(live, 'target')) if (upper(st.status_id) === rest.pause.status_id) {
          const ends = rest.pause.ends.split('-').map(Number), paused = rest.pause.paused.split('-').map(Number);
          ['year', 'month', 'day', 'hour', 'minute', 'second'].forEach((u, i) => { st[`status_ends_campaign_${u}`] = ends[i] ?? 0; st[`status_paused_campaign_${u}`] = paused[i] ?? 0; });
        }
      }
    }
  }
  playerInit.sort((a, b) => (b.init - a.init) || (b.tie - a.tie)); // uasort: stable
  const playerOrder = playerInit.map(p => p.id);
  creatures.sort((a, b) => phpCmp(b.cr, a.cr)); // usort: stable (PHP 8)
  const creatureOrder = creatures.map(c => c.id);
  let creatureInit = 0, creatureTie = 0;
  if (creatures.length) { const r = rollFor(creatures[0]!.id, 'creature'); creatureInit = r.total; creatureTie = r.tie; }
  const topPlayer = playerInit[0];
  const highestInit = topPlayer ? topPlayer.init : -999, highestTie = topPlayer ? topPlayer.tie : 0;
  const firstSide: 'players' | 'creatures' = highestInit > creatureInit || (highestInit === creatureInit && highestTie > creatureTie) ? 'players' : 'creatures';
  const alternate = (p: string[], c: string[]): string[] => {
    const out: string[] = [];
    for (let i = 0; i < Math.max(p.length, c.length); i++) {
      const [a, b] = firstSide === 'players' ? [p[i], c[i]] : [c[i], p[i]];
      if (a !== undefined) out.push(a);
      if (b !== undefined) out.push(b);
    }
    return out;
  };
  const mainOrder = alternate(playerOrder, creatureOrder);
  // ---- 4. lairs, bands, summons, the per-member reset ----
  const lairDc = (row: Row): number => { const v = scalar(ecOf(row).lair_dc); return phpNumeric(v) ? Number(v) : 0; };
  lairs.sort((a, b) => (lairDc(b) - lairDc(a)) || strcasecmp(nameOf(a), nameOf(b)));
  const lairOrder = lairs.map(idOf);
  if (!mainOrder.length) throw new PlanRefusal('no-combatants', 'No valid combatants found for initiative!');
  // gather_summons(): living, public members with a controller; a PC controller → the player block (roster order — SQL had no ORDER BY).
  const roster = byId();
  const summonP: string[] = [], summonC: string[] = [];
  for (const row of rosterOf(work)) {
    if (!isMember(row) || !living(row) || !phpEmpty(ecOf(row).encounter_creature_private)) continue;
    const ctl = upper(ecOf(row).controller_encounter_creature_id);
    if (!ctl) continue;
    const controller = roster.get(ctl);
    (controller && itemType(controller) === 'player_character' ? summonP : summonC).push(idOf(row));
  }
  const summonOrder = alternate(summonP, summonC);
  const membersOut: CombatStartPlan['members'] = [];
  const missingResetEffects = new Map<string, string[]>();
  const reset = (row: Row): { aps: number; aps_total: number } => {
    const absent = new Set<string>();
    const result = memberStartOfTurnReset(row, work, read, engine, absent);
    if (absent.size) {
      const fetchable = [...absent].filter(id => read.canFetchOptionalDefinition?.(id) !== false);
      if (!real && fetchable.length) throw new PlanMissing(fetchable);
      missingResetEffects.set(idOf(row), [...absent].map(id => 'definition-missing:' + id));
    }
    return result;
  };
  const rank = (ids: string[], base: number): void => {
    ids.forEach((id, i) => {
      const row = byId().get(id);
      if (!row) return;
      const r = reset(row);
      const ec = row.encounter_creatures as Row;
      Object.assign(ec, { aps: r.aps, aps_total: r.aps_total, weapon_attack: 0, head_attack: 0, reaction: 1, movement_pct: 0, creature_initiative: base - i });
      delete ec.aps_used;
      membersOut.push({ id, aps: r.aps, aps_total: r.aps_total, creature_initiative: base - i, weapon_attack: 0, head_attack: 0, reaction: 1, movement_pct: 0 });
    });
  };
  rank(lairOrder, LAIR_BAND);
  rank(mainOrder, MAIN_BAND);
  rank(summonOrder, SUMMON_BAND);
  if (missing.size) throw new PlanMissing([...missing]);
  for (const op of input.charges) { const r = byId().get(upper(op.holder)); if (r && isMember(r)) ap.setCharges(r, upper(op.item_id), op.to); }
  // ---- 5. the encounter flips ----
  const pointer = lairOrder[0] ?? mainOrder[0]!;
  Object.assign(bucketOf(work), { in_combat: 1, current_round: 1, current_encounter_creature_id_turn: pointer });

  // ---- 6. combat_begins contingencies (every RESIDENT's armed row — ft_fire_encounter_contingencies_on_combat_begin) ----
  const contingencies = contingencyFires('combat_begins', work, input, read, ap, roll, engine, missing, warnings, frozen.contingencySkip, real);
  if (missing.size) throw new PlanMissing([...missing]);

  // ---- 7. the FIRST combatant's start-of-turn block ----
  const firstRow = byId().get(pointer)!;
  const sot = engine.startOfTurn(firstRow, read.turn(work), { clockAdvanceSeconds: 0 });
  const legacy: string[] = [];
  for (const g of sot.gates) {
    if (!real && g.startsWith('definition-missing:')) {
      const id = upper(g.slice('definition-missing:'.length));
      if (read.canFetchOptionalDefinition?.(id) !== false) throw new PlanMissing([id]);
    }
    if (START_IGNORED_GATES.has(g) || START_TICK_GATES.has(g)) continue;
    legacy.push(g);
  }
  if (missing.size) throw new PlanMissing([...missing]);
  const base = reset(firstRow);
  const firstAps = sot.overrides.no_actions ? 0 : base.aps, firstReaction: 0 | 1 = sot.overrides.no_reactions ? 0 : 1;
  // R-130: run supported first-turn ticks unless a gate or prior synthetic pass froze the hook.
  let tick: PostedVerdict | null = null;
  let firstOffers: string[] | null = null;
  if (!legacy.length) {
    const frozenReason = real ? frozen.firstLegacy.get(pointer) : undefined;
    const t = frozenReason !== undefined ? frozenReason : firstTurnTicks(work, firstRow, firstAps, firstReaction, sot.removals, read, roll, engine, input.encounterId, real);
    const need = typeof t === 'string' ? missingFrom(t) : null;
    if (need && !real && read.canFetchOptionalDefinition?.(need) !== false) throw new PlanMissing([need]);
    if (typeof t === 'string') legacy.push(t); else if (!emptyVerdict(t)) tick = t;
    if (typeof t !== 'string') firstOffers = (t as { fortunateOffers?: string[] }).fortunateOffers ?? null;
  }
  const first: CombatStartPlan['first'] = legacy.length
    ? { id: pointer, legacy, aps: base.aps, aps_total: base.aps_total, reaction: 1, weapon_attack: 0, head_attack: 0, movement_pct: 0, removals: [], tick: null }
    : { id: pointer, legacy: null, aps: firstAps, aps_total: base.aps_total, reaction: firstReaction,
      weapon_attack: 0, head_attack: 0, movement_pct: 0, removals: sot.removals, tick };
  if (legacy.length) Object.defineProperty(first, SUPPORTED_FIRST, {
    value: { aps: firstAps, reaction: firstReaction, removals: sot.removals } satisfies SupportedFirst,
    enumerable: false,
  });
  // OWNER RULING 2026-10-03 (lane A11): the first combatant's deferred Fortunate — a NON-enumerable page-side field (never posted); combat-truth.ts
  // offers it after the start is saved
  if (!legacy.length && firstOffers?.length) Object.defineProperty(first, 'fortunateOffers', { value: firstOffers, enumerable: false });

  // ---- the pre-image of every column a posted verdict writes (the save's CAS — coordinator Codex round 4 #2) ----
  const ecBefore: Record<string, Record<string, unknown>> = {};
  const verdicts = [...begins.verdicts.map(e => e.verdict), ...rolls.map(e => e.verdict),
    ...contingencies.steps.flatMap(s => s.kind === 'fire' ? [s.verdict] : []), ...(first.tick ? [first.tick] : [])];
  for (const v of verdicts) for (const [id, changes] of Object.entries(v.ec)) {
    const key = upper(id), cols = (ecBefore[key] ??= {});
    for (const col of Object.keys(changes)) if (!(col in cols)) cols[col] = ecPreImage(preImage.get(key), col);
  }

  const result: { warnings: string[]; plan: CombatStartPlan } = {
    warnings,
    plan: {
      combat_id: combatId, started_by: input.startedBy ? upper(input.startedBy) : null,
      participants: input.participants.map(p => ({ id: upper(p.id), reason: p.reason })),
      begins,
      initiative: { rolls, first_side: firstSide, lairs: lairOrder, main: mainOrder, summons: summonOrder },
      members: membersOut,
      ec_before: ecBefore,
      encounter: { in_combat: 1, current_round: 1, current_encounter_creature_id_turn: pointer },
      contingencies, first,
    },
  };
  Object.defineProperty(result.plan, MISSING_RESET_EFFECTS, { value: missingResetEffects, enumerable: false });
  return result;
}

/** R-128 — the armed contingencies of every RESIDENT whose packed condition is `cond` (ft_fire_encounter_contingencies_on_combat_begin /
 *  _end → ft_fire_contingency_triggers): the fizzle / top-up ft_fire_contingency_triggers() judged, then the cast through
 *  resolveEngineFiredCasts (trigger mode, mana pre-paid, on its owner); each fire claims its row on the working copy and lands its verdict
 *  there (a later fire reads it). A fire the resolver hands back STAYS ARMED (never claimed, never re-rolled away) and is named. */
function contingencyFires(cond: 'combat_begins' | 'combat_ends', work: Row, input: { encounterId: string; rollPrivate: number }, read: CombatStartReaders,
  ap: CombatStartAppliers, roll: RollSource, engine: Pick<CombatStartEngine, 'engineFired'>, missing: Set<string>, warnings: string[],
  frozenSkip: ReadonlyMap<string, string> = new Map(), real = false): ContingencyPlan {
  const byId = (): Map<string, Row> => new Map(rosterOf(work).map(r => [idOf(r), r]));
  const members = (): Map<string, Row> => byId();
  const resolved = (v: AbilityUseVerdict, what: string): Resolved | string => {
    if (v.kind === 'refuse') return `${what}:refused:${v.message}`;
    if (v.kind !== 'resolved') { const id = missingFrom(v.reason); if (id && v.reason.startsWith('preflight:')) { missing.add(id); return 'missing'; } return v.reason; }
    const bad = unsavable(v, input.encounterId);
    return bad ? `${what}:unsavable:${bad}` : v;
  };
  const contingencies: ContingencyPlan = { steps: [], skipped: [] };
  for (const owner of rosterOf(work)) {
    if (!phpEmpty(ecOf(owner).encounter_creature_deleted)) continue;
    for (const st of [...statusRows(owner, 'target')]) { // a copy: a fire claims (removes) its row from the live list
      if (!CONTINGENCY_ARMING_IDS.includes(statusItemId(st)) || !phpEmpty(st.status_deleted)) continue;
      const parsed = contingencyParse(st.status_casting_targets_arr);
      if (parsed.cond !== cond) continue;
      const statusId = upper(st.status_id), ownerId = idOf(owner), spellId = upper(st.status_origin_id);
      // an earlier step of this boundary may have removed this row (its own fire / fizzle cascade): re-read the live list
      if (!statusRows(byId().get(ownerId) ?? owner, 'target').some(x => upper(x.status_id) === statusId)) continue;
      if (!UUID_RE.test(statusId) || !UUID_RE.test(spellId) || isSelfStatusOrigin(statusId, spellId)) {
        // an armed row this page cannot read is REPORTED (it stays armed), never silently passed over (Codex review A2 #4)
        contingencies.skipped.push({ status_id: statusId || '?', owner_id: ownerId, reason: 'contingency-row-unreadable' });
        warnings.push(`${nameOf(owner) || 'A creature'}'s Contingency could not be read — it stays armed.`);
        continue;
      }
      if (frozenSkip.has(statusId)) {
        contingencies.skipped.push({ status_id: statusId, owner_id: idOf(owner), reason: frozenSkip.get(statusId)! });
        warnings.push(`${nameOf(owner) || 'A creature'}'s Contingency could not fire in the browser (${frozenSkip.get(statusId)}) — it stays armed.`);
        continue;
      }
      const spell = read.ability.definition(spellId);
      if (!spell) { missing.add(spellId); continue; }
      const ownerNow = byId().get(ownerId) ?? owner;
      const decision = contingencyManaPlan(parsed, spell, phpInt(read.exit.value(spell, 'spell_level') ?? 0),
        phpInt(ecOf(ownerNow).mana ?? 0), ecOf(ownerNow).mana_total, { holder: ownerNow, read: read.exit });
      if (decision.kind === 'fizzle') {
        contingencies.steps.push({ kind: 'fizzle', status_id: statusId, owner_id: ownerId, mana: decision.mana, reason: decision.reason });
        ap.removeStatus(ownerNow, statusId);
        if (decision.mana) (ownerNow.encounter_creatures as Row).mana = decision.mana.after;
        continue;
      }
      const { castLevel, mana: topUp } = decision;
      if (topUp) (ownerNow.encounter_creatures as Row).mana = topUp.after;
      const v = engine.engineFired({ encounterId: input.encounterId, rollPrivate: input.rollPrivate, contingencies: [{ ownerId, spellId, castLevel }] }, work, members(), read.ability, roll);
      const r = resolved(v, 'contingency');
      if (r === 'missing') { if (topUp) (ownerNow.encounter_creatures as Row).mana = topUp.before; continue; }
      if (typeof r === 'string') {
        // Not this lane's yet (an armed UPCAST, a channel the commit cannot save): it stays ARMED — never claimed, never re-rolled away.
        // On the real dice only a hand-back a synthetic pass already met (frozen above) is acceptable; a new one is outcome-dependent.
        if (real) throw new PlanRefusal('contingency-outcome-dependent:' + r, 'A contingency here cannot be decided in the browser yet (' + r + ').');
        if (topUp) (ownerNow.encounter_creatures as Row).mana = topUp.before;
        contingencies.skipped.push({ status_id: statusId, owner_id: ownerId, reason: r });
        warnings.push(`${nameOf(ownerNow) || 'A creature'}'s Contingency could not fire in the browser (${r}) — it stays armed.`);
        continue;
      }
      const p = post(r, read.uuid);
      ap.removeStatus(ownerNow, statusId);
      applyVerdict(work, p, ap);
      contingencies.steps.push({ kind: 'fire', status_id: statusId, owner_id: ownerId, spell_id: spellId, mana: topUp, verdict: p });
    }
  }
  return contingencies;
}

/** R-128 (lane N5b) — the EXIT's combat_ends contingencies, decided by the page: the fires on extreme synthetic dice first (a refusal or a
 *  missing definition before a real die — the A7 rule), then on the real dice. Posted with the exit plan; the commit claims and saves. */
export function planCombatEndContingencies(input: { encounterId: string; rollPrivate: number }, encounter: Row, read: CombatStartReaders, ap: CombatStartAppliers,
  roll: RollSource, engine: Pick<CombatStartEngine, 'engineFired'> = COMBAT_START_ENGINE):
  { kind: 'plan'; contingencies: ContingencyPlan; warnings: string[] } | { kind: 'missing'; ids: string[] } | { kind: 'refuse'; reason: string; message: string } {
  const frozen = new Map<string, string>();
  try {
    for (const pattern of EXIT_DRY_PATTERNS) {
      const missing = new Set<string>();
      const dry = contingencyFires('combat_ends', clone(encounter), input, read, ap, syntheticDice(pattern), engine, missing, [], frozen);
      if (missing.size) return { kind: 'missing', ids: [...missing] };
      for (const k of dry.skipped) frozen.set(k.status_id, k.reason);
    }
    const missing = new Set<string>(), warnings: string[] = [];
    const contingencies = contingencyFires('combat_ends', clone(encounter), input, read, ap, roll, engine, missing, warnings, frozen, true);
    if (missing.size) return { kind: 'refuse', reason: 'definitions-outcome-dependent', message: 'A contingency here needs data the page has not loaded — try again in a moment.' };
    return { kind: 'plan', contingencies, warnings };
  } catch (error) {
    if (error instanceof PlanRefusal) return { kind: 'refuse', reason: error.reason, message: error.userMessage };
    throw error;
  }
}

/** roll_initiative_process_resting_status() for one PC: no Resting row → nothing (not even waking); else Sleeping removed, and the
 *  Resting row removed (permanent), left (already paused), or paused with its end pushed out one hour. */
export function restingInterruption(row: Row, read: Pick<CombatStartReaders, 'clockNow' | 'addSeconds'>): RestingInterruption | null {
  const live = statusRows(row, 'target').filter(s => phpEmpty(s.status_deleted));
  const restingRow = live.find(s => statusItemId(s) === RESTING_STATUS_ID);
  if (!restingRow) return null;
  const target = idOf(row);
  const remove = live.filter(s => statusItemId(s) === SLEEPING_STATUS_ID).map(s => upper(s.status_id));
  const cols = ['year', 'month', 'day', 'hour', 'minute', 'second'] as const;
  const ends = cols.map(u => phpInt(restingRow[`status_ends_campaign_${u}`] ?? 0));
  if (ends.every(v => v === 0)) return { target, remove: [...remove, upper(restingRow.status_id)], pause: null };
  if (statusPaused(restingRow)) return { target, remove, pause: null };
  const now = read.clockNow();
  if (!now) return { target, remove, pause: null }; // PHP: no campaign → the rest is woken, not paused
  const endClock: CampaignClock = { year: ends[0]!, month: ends[1]!, day: ends[2]!, hour: ends[3]!, minute: ends[4]!, second: ends[5]! };
  const next = read.addSeconds(endClock, 3600);
  const join = (c: CampaignClock): string => cols.map(u => c[u]).join('-');
  return { target, remove, pause: { status_id: upper(restingRow.status_id), ends_before: join(endClock), ends: join(next), paused: join(now) } };
}
